import {
  CopyField,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  Card,
  CardHeader,
  TableCard,
  Tag,
  type Column,
} from '@sparkery/ui';
import type { DiscountCodeRow, ReferResponse, ReferralLinkRow } from '@sparkery/contracts';
import { dateWindow, useRefer } from '../../lib/workspace.ts';
import { CODE_STATUS_TONE, plural, titleCase } from '../../lib/report.tsx';
import './refer.css';

/**
 * A partner's links and codes.
 *
 * Two tables, never one. A link is a UTM-tagged URL measured by GA4; a code is what
 * attributes a sale. Presenting them as one list would invite the reading that a link's
 * traffic and a code's usage are the same funnel, which they are not.
 *
 * The codes shown here are the ones Sparkery has MAPPED to this partner. Shopify holds
 * the discount itself but has no notion of who it belongs to, so a code missing from
 * this list earns nothing however many customers type it in.
 */
export function ReferView() {
  const refer = useRefer();
  const data = refer.data;

  const linkColumns: Column<ReferralLinkRow>[] = [
    { id: 'label', header: 'Label', cell: (row) => row.label },
    {
      id: 'url',
      header: 'Link',
      cell: (row) => <span className="referUrl">{row.url}</span>,
    },
    {
      id: 'channel',
      header: 'Channel',
      secondary: true,
      cell: (row) => (
        <span className="referChannel">
          {row.channel}
          {row.placement ? ` · ${row.placement}` : ''}
        </span>
      ),
    },
    {
      id: 'sessions',
      // "Channel sessions", not "Clicks": GA4 measures the channel, so two links
      // sharing one show the same figure — and a session is not a click.
      header: 'Channel sessions',
      numeric: true,
      cell: (row) =>
        row.channelSessions === null ? (
          <span className="referUnsynced" title="Analytics has not synced yet">
            —
          </span>
        ) : (
          row.channelSessions.toLocaleString('en-GB')
        ),
    },
  ];

  const codeColumns: Column<DiscountCodeRow>[] = [
    {
      id: 'code',
      header: 'Discount code',
      cell: (row) => (
        <span className="referCode">
          <strong>{row.code}</strong>
          <span className="referCodeWindow">{dateWindow(row.startsAt, row.endsAt)}</span>
        </span>
      ),
    },
    { id: 'amount', header: 'Amount', cell: (row) => row.amountText },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <Tag tone={CODE_STATUS_TONE[row.status]}>{titleCase(row.status)}</Tag>,
    },
    {
      id: 'usage',
      header: 'Usage',
      numeric: true,
      // ∞ rather than a blank or a 0: an unlimited code has no limit, which is not the
      // same as a limit of nothing.
      cell: (row) => `${row.used}/${row.usageLimit ?? '∞'}`,
    },
  ];

  return (
    <>
      <PageHeader title="Refer" subtitle="Create and share referral links" />

      {refer.isError ? (
        <ErrorState
          title="Could not load your links"
          {...(refer.error instanceof Error ? { detail: refer.error.message } : {})}
          onRetry={() => void refer.refetch()}
        />
      ) : null}

      {/* The dark panel from the prototype: the two things a partner came here to copy,
          lifted out of the tables below. */}
      <Card variant="dark" className="referPanel">
        <CardHeader title="Your referral link" onDark />
        <div className="referPanelFields">
          <CopyField label="Latest referral link" value={data?.primaryLink ?? '—'} />
          <CopyField label="Latest discount code" value={data?.primaryCode ?? '—'} size="compact" />
        </div>
        <p className="referPanelNote">
          {data
            ? `Every sale made through your links is credited to ${data.partnerName}.`
            : 'Loading your referral details.'}
        </p>
      </Card>

      {/* Count and session total in one line on the right, as the prototype has it.
          Two separate right-hand notes would compete for the same corner. */}
      <TableCard title="Referral links" meta={linksMeta(data)}>
        <DataTable
          caption="Referral links"
          columns={linkColumns}
          rows={data?.links ?? []}
          rowKey={(row) => row.id}
          loading={refer.isPending}
          empty={
            <EmptyState
              title="No referral links yet"
              body="Links are created by Sparkery when a partner is onboarded."
            />
          }
        />
      </TableCard>

      <TableCard
        title="Discount codes"
        meta={
          data
            ? `${data.codes.filter((code) => code.status === 'active').length} active`
            : undefined
        }
      >
        <DataTable
          caption="Discount codes"
          columns={codeColumns}
          rows={data?.codes ?? []}
          rowKey={(row) => row.id}
          loading={refer.isPending}
          empty={
            <EmptyState
              title="No discount codes yet"
              // "attribute a sale", not "earn commission": a fixed-fee partner's
              // referrals are attributed and paid for, just not per-order.
              body="Sparkery maps a discount code to your account before it can attribute a sale."
            />
          }
        />
      </TableCard>
    </>
  );
}

/**
 * "3 links · 70,219 sessions, last 30 days".
 *
 * The session figure is programme-wide, not per link — GA4 attributes a session to a
 * campaign, and several links share one campaign — so it is stated once here rather
 * than implied by a column.
 */
function linksMeta(data: ReferResponse | undefined): string | undefined {
  if (!data) return undefined;
  const count = plural(data.links.length, 'link');
  if (data.sessions === null) return `${count} · sessions appear once analytics has synced`;
  // Not "last 30 days": the figure is every session since the programme opened, and the
  // caption said otherwise for as long as this view has existed.
  const channels = plural(data.channels.length, 'channel');
  return `${count} · ${data.sessions.toLocaleString('en-GB')} sessions across ${channels}`;
}
